// src/data/wall.ts
// ----------------
// THE WALL — Layer 3 (repo assertions).
// docs/staff-caseworker-separation-plan.md §7 · CLAUDE.md "Design principle — the
// Zephyr / DFV Service wall".
//
// Zephyr staff and DFV Service contacts are two populations that must never mix:
//   • Zephyr staff identity  → profiles.staff_first_name / _last_name / _user_email / _mobile
//   • DFV contact identity   → their user_shelters membership row
//   • a Zephyr person may NEVER hold a user_shelters row
//
// The wall is HELD by database triggers (db-142) — those are authoritative and cannot
// be bypassed. This module exists purely so a developer finds out at the CALL SITE, in
// plain English, instead of meeting a Postgres 23514 as an opaque toast three layers up.
//
// ⚠️ THESE CHECKS ARE DELIBERATELY INCOMPLETE, and that is fine.
// A client-side check only sees the object being written, never the stored row — so it
// cannot know the role of a profile being patched unless the patch itself carries one.
// Anything this misses, db-142 still catches. NEVER weaken db-142 on the strength of
// these; and never "fix" a failure here by widening the allow-list without reading the
// plan doc first.

const DOC = "docs/staff-caseworker-separation-plan.md";

// Distinct class so a caller (or a test) can tell a wall breach apart from an ordinary
// failure. It is a PROGRAMMING error, not a user error — hence a throw rather than the
// repos' usual { error } return.
export class WallViolationError extends Error {
  constructor(message: string) {
    super(`THE WALL: ${message}\nSee ${DOC}`);
    this.name = "WallViolationError";
  }
}

// The four staff-identity columns policed by the db-142 profiles trigger.
// staff_colour / show_on_staff_list / staff_notes are deliberately NOT policed — they
// are display/admin attributes, meaningless rather than wrong on a caseworker.
const STAFF_IDENTITY_COLS = [
  "staff_first_name",
  "staff_last_name",
  "staff_user_email",
  "staff_mobile",
] as const;

const ZEPHYR_DOMAIN = "@zephyreducation.com.au";

// A profiles insert/update must not put staff identity on a caseworker account.
//
// LIMIT: only fires when the written object ITSELF says role === 'shelter'. A patch that
// sets staff_* without a role can't be judged here (the stored role isn't visible) — that
// case belongs to db-142.
export function assertNoCaseworkerIdentity(row: unknown, context: string): void {
  if (!row || typeof row !== "object") return;
  const r = row as Record<string, unknown>;
  if (r.role !== "shelter") return;

  const offending = STAFF_IDENTITY_COLS.filter(
    (k) => r[k] !== undefined && r[k] !== null && r[k] !== ""
  );
  if (offending.length === 0) return;

  throw new WallViolationError(
    `${context} tried to write ${offending.join(", ")} on a role='shelter' account. ` +
      "profiles.staff_* is Zephyr-staff-only. A DFV Service caseworker's name, email and " +
      "phone belong on their user_shelters membership contact — write them there instead."
  );
}

// A user_shelters insert/update must not create a DFV Service contact for a Zephyr person.
//
// LIMIT: only the EMAIL test is possible client-side. The other two ways a row qualifies —
// user_id resolving to an internal role, and profiles.is_consultant — need a profiles read
// the caller may not be permitted to make, so they are left to db-142 (which does them
// under SECURITY DEFINER).
export function assertNotZephyrContact(row: unknown, context: string): void {
  if (!row || typeof row !== "object") return;
  const r = row as Record<string, unknown>;
  const email = typeof r.email === "string" ? r.email : "";
  if (!email.toLowerCase().endsWith(ZEPHYR_DOMAIN)) return;

  throw new WallViolationError(
    `${context} tried to create a DFV Service contact for ${email}. ` +
      "user_shelters holds DFV Service contacts only — a Zephyr address can never be one. " +
      "If a staff member needs to SEE a service, that is access control, not a contact record."
  );
}

// Array form (bulk inserts).
export function assertNoZephyrContacts(rows: unknown, context: string): void {
  if (!Array.isArray(rows)) return;
  for (const row of rows) assertNotZephyrContact(row, context);
}
